// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
} from '@mui/material';
import PropTypes from 'prop-types';
import { ReactComponent as CloseIcon } from '../../../../assets/Icons/lucideIcons/x.svg';
import { useDeleteDocumentMutation } from '../../../../features/document/api/documentInventoryQueries';
import {
  CHECKLIST_TYPES,
  CHECKLIST_DATA_TYPE,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
} from '../../../../const/CommonConst';
import AttributeField from '../AttributeField';
import useAttributeEditForm from '../../../../hooks/useAttributeEditForm';
import { useUploadDocumentContext } from '../../../../context/DocumentUpload/UploadDocumentContext';

const attributeRequiresTeamUsers = ['assignedUser'];

/**
 * Modal component for editing attribute values
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.handleClose - Function to close the modal
 * @param {Object} props.attribute - The attribute being edited
 * @param {any} props.value - The current value of the attribute
 * @param {Function} props.handleSave - Function to save the new value
 * @param {Array} props.usersList - List of available users (for User type)
 * @param {Array} props.dropdownOptions - List of options for dropdown fields
 * @returns {React.ReactElement} The EditValueModal component
 */
const EditValueModal = ({
  open,
  handleClose,
  attribute,
  groupDetails = EMPTY_OBJECT,
  value,
  handleSave,
  usersList = EMPTY_ARRAY,
  teamUsersList = EMPTY_ARRAY,
  entityData = EMPTY_OBJECT,
  onCalculateAttribute,
  formDetails = EMPTY_OBJECT,
  attributeValues = EMPTY_OBJECT,
  isHDFCUAMEnabled = false,
  currentModelStatus = EMPTY_OBJECT,
  modelTemplateDetails = EMPTY_OBJECT,
}) => {
  const saveSearch =
    attribute?.data_type === CHECKLIST_DATA_TYPE &&
    attribute?.checklist_type === CHECKLIST_TYPES?.SAVE_SEARCH;
  const { handleStopUpload, activeBatches = EMPTY_ARRAY } =
    useUploadDocumentContext();
  const { mutateAsync: deleteDocument } = useDeleteDocumentMutation();
  // Use the new attribute edit form hook
  const {
    handleSave: handleFormSave,
    handleValueChange,
    getCurrentValue,
    entityDetailsFormikForm,
  } = useAttributeEditForm({
    attribute,
    value,
    onSave: handleSave,
    onCancel: handleClose,
    formDetails,
    attributeValues,
    isHDFCUAMEnabled,
    currentModelStatus,
    usersList,
    entityData,
  });
  const [hasFileError, setHasFileError] = React.useState(false);
  const finalUsersList = useMemo(() => {
    if (
      attribute?.data_type === 'User' &&
      attributeRequiresTeamUsers.includes(attribute?.name)
    ) {
      return teamUsersList;
    }
    return usersList;
  }, [attribute?.data_type, attribute?.name, usersList, teamUsersList]);

  /**
   * Handles saving the edited value
   * @function
   * @returns {void}
   */
  const onSave = useCallback(() => {
    handleFormSave();
  }, [handleFormSave]);

  /**
   * Handles modal cancellation by identifying and deleting any newly uploaded documents
   * that haven't been saved to this attribute and stopping any in-progress upload batches.
   * @function
   * @returns {void}
   */
  const onCancel = useCallback(() => {
    // If it's a Document attribute, stop in-progress uploads and delete any new files
    if (attribute?.data_type === 'Document') {
      // Stop any in-progress uploads for this attribute
      activeBatches.forEach((batch) => {
        (batch.data || []).forEach((f) => {
          if (f.attribute_id === attribute?.id && f.key) {
            handleStopUpload(f.key);
          }
        });
      });

      const currentValue = getCurrentValue();
      const currentIDs = Array.from(
        new Set(
          (Array.isArray(currentValue) ? currentValue : [currentValue])
            .map((doc) => doc?.document_id)
            .filter(Boolean)
            .map(String)
        )
      );

      const initialIDs = new Set(
        (Array.isArray(value) ? value : [value])
          .map((doc) => doc?.document_id)
          .filter(Boolean)
          .map(String)
      );

      currentIDs.forEach((id) => {
        if (!initialIDs.has(id)) {
          deleteDocument({ document_id: id });
        }
      });
    }
    handleClose();
  }, [
    attribute,
    getCurrentValue,
    value,
    deleteDocument,
    handleClose,
    activeBatches,
    handleStopUpload,
  ]);

  /**
   * Renders the attribute field using the reusable AttributeField component
   * @function
   * @returns {React.ReactElement} The rendered attribute field
   */
  const renderInput = useCallback(() => {
    if (!attribute) return null;

    const currentValue = getCurrentValue();

    return (
      <AttributeField
        attribute={attribute}
        value={currentValue}
        onChange={handleValueChange}
        usersList={finalUsersList}
        entityData={entityData}
        entityDetailsFormikForm={entityDetailsFormikForm}
        onCalculateAttribute={onCalculateAttribute}
        onFileStatusChange={setHasFileError}
        groupDetails={groupDetails}
        modelTemplateDetails={modelTemplateDetails}
      />
    );
  }, [
    attribute,
    getCurrentValue,
    handleValueChange,
    usersList,
    finalUsersList,
    entityDetailsFormikForm,
    setHasFileError,
  ]);

  return (
    <Dialog
      onClose={onCancel}
      open={open}
      maxWidth={saveSearch ? 'md' : 'sm'}
      fullWidth={true}
      aria-labelledby="edit-attribute-value-modal"
    >
      <DialogTitle>
        <Box component="span" aria-live="polite" aria-atomic="true">
          Edit Attribute
        </Box>
      </DialogTitle>
      <IconButton
        color="primary"
        aria-label="Close dialog"
        onClick={onCancel}
        sx={{
          position: 'absolute',
          right: 16,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={saveSearch ? { py: 0 } : {}}>
        <Grid container spacing={2}>
          <Grid size={12}>{renderInput()}</Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="outlined" title="Cancel">
          Cancel
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          title={hasFileError ? 'Uploading files...' : 'Save'}
          disabled={
            hasFileError ||
            Boolean(entityDetailsFormikForm?.errors?.[attribute?.id])
          }
        >
          {hasFileError ? 'Uploading...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
EditValueModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    data_type: PropTypes.string,
    input_control_type: PropTypes.string,
    label: PropTypes.string,
    attribute_origin: PropTypes.string,
    checklist_type: PropTypes.string,
  }).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.object,
  ]),
  handleSave: PropTypes.func.isRequired,
  usersList: PropTypes.arrayOf(PropTypes.shape({})),
  teamUsersList: PropTypes.arrayOf(PropTypes.shape({})),
  entityData: PropTypes.shape({
    entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity_type: PropTypes.string,
  }).isRequired,
  onCalculateAttribute: PropTypes.func.isRequired,
  formDetails: PropTypes.shape({}),
  attributeValues: PropTypes.shape({}),
  isHDFCUAMEnabled: PropTypes.bool,
  currentModelStatus: PropTypes.shape({
    currentState: PropTypes.string,
  }),
  groupDetails: PropTypes.shape({
    isGroupAdmin: PropTypes.bool,
    userName: PropTypes.string,
    userId: PropTypes.number,
    users: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.number,
        username: PropTypes.string,
      })
    ),
  }),
  modelTemplateDetails: PropTypes.shape({}),
};
export default EditValueModal;
